import cn from 'classnames'

/*
 * The circle at the top of every screen: a flat two-tone icon on a tinted
 * disc. Light strokes on the dark ground, a translucent purple for the
 * secondary shape, the ground colour for the primary one. No animation.
 */
export type TArt =
   | 'connect'
   | 'you'
   | 'team'
   | 'calendar'
   | 'budget'
   | 'payment'
   | 'area'
   | 'number'
   | 'stops'
   | 'business'
   | 'launch'

const B = 'var(--ob-light)'
const T = 'rgba(144, 97, 218, 0.45)'
const W = '#17122a'
const P = 'var(--ob-primary)'
const s = {
   stroke: B,
   strokeWidth: 2,
   strokeLinecap: 'round' as const,
   strokeLinejoin: 'round' as const,
}
const n = { ...s, fill: 'none' }
const tick = {
   stroke: '#fff',
   strokeWidth: 2,
   strokeLinecap: 'round' as const,
   strokeLinejoin: 'round' as const,
   fill: 'none',
}

const ART: Record<TArt, JSX.Element> = {
   connect: (
      <g>
         <rect x="8" y="22" width="26" height="20" rx="6" fill={T} {...s} />
         <circle cx="21" cy="32" r="4" fill={W} {...s} />
         <path d="M34 32h16M50 32v6M44 32v4" {...n} />
         <circle cx="52" cy="26" r="3" fill={W} {...s} />
      </g>
   ),
   you: (
      <g>
         <circle cx="30" cy="24" r="9" fill={W} {...s} />
         <path d="M12 52a18 18 0 0 1 36 0" fill={T} {...s} />
         <circle cx="49" cy="46" r="8" fill={P} />
         <path d="M45.5 46l2.5 2.5 5-5" {...tick} />
      </g>
   ),
   team: (
      <g>
         <circle cx="24" cy="23" r="8" fill={W} {...s} />
         <path d="M8 49a16 16 0 0 1 32 0" fill={T} {...s} />
         <circle cx="44" cy="25" r="6" fill={W} {...s} />
         <path d="M42 36a13 13 0 0 1 14 13" {...n} />
      </g>
   ),
   calendar: (
      <g>
         <rect x="10" y="14" width="44" height="40" rx="7" fill={W} {...s} />
         <path
            d="M10 21a7 7 0 0 1 7-7h30a7 7 0 0 1 7 7v5H10z"
            fill={T}
            stroke="none"
         />
         <path d="M10 26h44M22 9v10M42 9v10" {...n} />
         <path d="M24 42l6 6 11-11" {...n} />
      </g>
   ),
   budget: (
      <g>
         <path d="M12 44a20 20 0 1 1 40 0z" fill={T} {...s} />
         <path d="M32 44l9-11" {...n} />
         <circle cx="32" cy="44" r="3" fill={W} {...s} />
      </g>
   ),
   payment: (
      <g>
         <rect x="8" y="16" width="48" height="32" rx="6" fill={W} {...s} />
         <rect x="8" y="24" width="48" height="8" fill={P} stroke="none" />
         <rect x="15" y="38" width="12" height="4" rx="2" fill={T} />
      </g>
   ),
   area: (
      <g>
         <ellipse cx="32" cy="52" rx="16" ry="4" fill={T} />
         <path
            d="M32 50s-15-13-15-25a15 15 0 0 1 30 0c0 12-15 25-15 25z"
            fill={W}
            {...s}
         />
         <circle cx="32" cy="25" r="5" fill={P} />
      </g>
   ),
   number: (
      <g>
         <rect x="16" y="8" width="26" height="48" rx="6" fill={W} {...s} />
         <path d="M26 14h6" {...n} />
         <path
            d="M30 42h-2a6 6 0 0 1-6-6v-6a6 6 0 0 1 6-6h8a6 6 0 0 1 6 6v6a6 6 0 0 1-6 6h-2l-4 4z"
            fill={T}
            {...s}
         />
         <circle cx="29" cy="30" r="1.5" fill={B} />
         <circle cx="34" cy="30" r="1.5" fill={B} />
         <circle cx="47" cy="18" r="6" fill={P} />
      </g>
   ),
   stops: (
      <g>
         <path
            d="M32 8l20 7v16c0 12-8 20-20 25C20 51 12 43 12 31V15z"
            fill={T}
            {...s}
         />
         <path
            d="M32 18l12 4v9c0 8-5 13-12 16-7-3-12-8-12-16v-9z"
            fill={W}
            {...s}
         />
         <path d="M26 36l12-10" {...n} />
      </g>
   ),
   business: (
      <g>
         <path d="M16 8h22l10 10v38H16z" fill={W} {...s} />
         <path d="M38 8v10h10" fill={T} {...s} />
         <path d="M23 30h18M23 38h18M23 46h10" {...n} />
         <circle cx="46" cy="48" r="8" fill={P} />
         <path d="M42.5 48l2.5 2.5 5-5" {...tick} />
      </g>
   ),
   launch: (
      <g>
         <path
            d="M32 6c8 6 12 16 12 28l-6 6H26l-6-6c0-12 4-22 12-28z"
            fill={W}
            {...s}
         />
         <circle cx="32" cy="26" r="5" fill={T} {...s} />
         <path d="M20 34l-8 6 8 2M44 34l8 6-8 2" fill={T} {...s} />
         <path d="M26 46l6 10 6-10" fill={P} stroke="none" />
      </g>
   ),
}

export const Circle = ({
   kind,
   size = 112,
   className,
}: {
   kind: TArt
   size?: number
   className?: string
}) => (
   <div
      className={cn(
         'ob-disc flex shrink-0 items-center justify-center rounded-full bg-[var(--ob-primary-tint)]',
         className
      )}
      style={{ width: size, height: size }}
      data-testid="ob-circle"
      data-kind={kind}
      aria-hidden
   >
      <svg width={size * 0.52} height={size * 0.52} viewBox="0 0 64 64">
         {ART[kind]}
      </svg>
   </div>
)
